import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {HeadingNode} from '@lexical/rich-text';
import {$getRoot, $isElementNode, NodeKey} from 'lexical';
import {useCallback, useEffect} from 'react';

import {EnhancedHeadingNode} from './EnhancedHeadingNode';
import {$createHeadingChevronDecoratorNode} from './HeadingChevronDecoratorNode';

/**
 * ChevronPlugin manages chevron decorator nodes for headings
 * to enable collapse/expand functionality.
 */
export const ChevronPlugin = () => {
  const [editor] = useLexicalComposerContext();

  const addChevronNodesToHeadings = useCallback(() => {
    editor.update(() => {
      const root = $getRoot();
      const processedHeadings = new Set<NodeKey>();

      // Recursively find all headings in the document
      const findHeadings = (node: any) => {
        // Only process actual heading elements with proper tags
        if (
          $isElementNode(node) &&
          (node instanceof HeadingNode || node instanceof EnhancedHeadingNode) &&
          node.getTag &&
          ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(node.getTag())
        ) {
          const headingId =
            node instanceof EnhancedHeadingNode ? node.getHeadingId() : node.getKey();

          // Skip if we've already processed this heading
          if (processedHeadings.has(headingId)) {
            return;
          }
          processedHeadings.add(headingId);

          // Check if this heading already has a chevron node
          const children = node.getChildren();
          const hasChevron = children.some(
            (child) =>
              child.getType() === 'HeadingChevron' && (child as any).getHeadingId() === headingId
          );

          // Only add chevron if the heading has text content
          const hasTextContent = node.getTextContent().trim().length > 0;

          // Add chevron decorator node if not present and has text
          if (!hasChevron && hasTextContent) {
            const chevronNode = $createHeadingChevronDecoratorNode(headingId);
            const firstChild = node.getFirstChild();
            if (firstChild) {
              firstChild.insertBefore(chevronNode);
            } else {
              node.append(chevronNode);
            }
          }

          // Remove chevron if heading becomes empty
          if (!hasTextContent) {
            const nodeChildren = node.getChildren();
            nodeChildren.forEach((child) => {
              if (child.getType() === 'HeadingChevron') {
                child.remove();
              }
            });
          }
        }

        // Recursively check children
        if (node.getChildren) {
          node.getChildren().forEach(findHeadings);
        }
      };

      // Start from root and search recursively
      findHeadings(root);
    });
  }, [editor]);

  useEffect(() => {
    // Initial setup
    addChevronNodesToHeadings();

    // Handle when headings are created or changed
    const removeNodeTransform = editor.registerNodeTransform(EnhancedHeadingNode, () => {
      addChevronNodesToHeadings();
    });

    // Listen for editor updates - add chevron nodes when needed
    const removeUpdateListener = editor.registerUpdateListener(({dirtyElements, dirtyLeaves}) => {
      // Add chevron nodes when there are structural changes
      const hasStructuralChanges = dirtyElements.size > 0 || dirtyLeaves.size > 0;

      if (hasStructuralChanges) {
        addChevronNodesToHeadings();
      }
    });

    return () => {
      removeNodeTransform();
      removeUpdateListener();
    };
  }, [editor, addChevronNodesToHeadings]);

  return null; // This plugin doesn't render anything
};
